import React from "react";
import {
    Rocket,
    Award,
    ClipboardCheck,
    FileText,
    Users,
    TrendingUp,
} from "lucide-react";
import { highlightBrand } from "../utils/brandUtils";
import GuideLayout, { type GuideSection } from "../components/pages/GuideLayout";

const UserGuidePage: React.FC = () => {
    const sections: GuideSection[] = [
        {
            id: "getting-started",
            title: "Getting Started",
            icon: <Rocket className="w-5 h-5 text-emerald-600" />,
            content: (
                <div className="space-y-4">
                    <p>
                        Welcome to {highlightBrand("LAB ZER0")}! Our platform is designed to help your laboratory track and reduce its environmental impact through structured assessments and actionable checklists.
                    </p>
                    <ul className="list-disc pl-5 space-y-2">
                        <li><strong>Join a Lab:</strong> From your dashboard, you can join an existing lab within your organisation or request your Admin to add you.</li>
                        <li><strong>Lab Overview:</strong> Once in a lab, you'll see your lab's current status, certification level, and upcoming tasks.</li>
                    </ul>
                </div>
            ),
        },
        {
            id: "certification-tiers",
            title: "Certification Tiers",
            icon: <Award className="w-5 h-5 text-amber-600" />,
            content: (
                <div className="space-y-4">
                    <p>Your sustainability journey is divided into three progressive tiers:</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="p-4 rounded-xl border border-amber-200 bg-amber-50/50">
                            <h4 className="font-bold text-amber-800 mb-2">Bronze</h4>
                            <p className="text-sm">Establish your baseline and implement initial foundational actions.</p>
                        </div>
                        <div className="p-4 rounded-xl border border-slate-300 bg-slate-50/50">
                            <h4 className="font-bold text-slate-700 mb-2">Silver</h4>
                            <p className="text-sm">Advance to more complex energy and waste reduction measures.</p>
                        </div>
                        <div className="p-4 rounded-xl border border-yellow-400 bg-yellow-50/50">
                            <h4 className="font-bold text-yellow-700 mb-2">Gold</h4>
                            <p className="text-sm">Achieve peak sustainability performance and lead by example.</p>
                        </div>
                    </div>
                </div>
            ),
        },
        {
            id: "assessments",
            title: "Performing Assessments",
            icon: <ClipboardCheck className="w-5 h-5 text-blue-600" />,
            content: (
                <div className="space-y-4">
                    <p>Assessments are the core of the platform. They help us understand where you are and how much you've improved.</p>
                    <div className="space-y-3">
                        <div className="flex gap-3">
                            <div className="mt-1 font-bold text-blue-600">01.</div>
                            <div>
                                <strong>Baseline Assessment:</strong> Completed once when you start. It captures your starting point for energy, water, and waste.
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <div className="mt-1 font-bold text-blue-600">02.</div>
                            <div>
                                <strong>Checklists:</strong> Tailored tasks for your lab type (Wet or Dry). Complete these to earn points towards certification.
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <div className="mt-1 font-bold text-blue-600">03.</div>
                            <div>
                                <strong>Savings Assessment:</strong> Re-evaluate your metrics after implementing changes to see your real-world impact.
                            </div>
                        </div>
                    </div>
                </div>
            ),
        },
        {
            id: "documentation",
            title: "Evidence & Documentation",
            icon: <FileText className="w-5 h-5 text-purple-600" />,
            content: (
                <div className="space-y-4">
                    <p>For certain actions, you'll need to provide evidence. This ensures the integrity of the certification process.</p>
                    <ul className="list-disc pl-5 space-y-2">
                        <li><strong>Uploads:</strong> Attach photos, energy bills, or policy documents directly to the relevant checklist item.</li>
                        <li><strong>Comments:</strong> Use comments to explain how your lab implemented a specific action.</li>
                    </ul>
                </div>
            ),
        },
        {
            id: "auditing",
            title: "Peer Auditing",
            icon: <Users className="w-5 h-5 text-teal-600" />,
            content: (
                <div className="space-y-4">
                    <p>We use a peer-audit model to foster collaboration and knowledge sharing across the organisation.</p>
                    <p>
                        As a lab member, you might be assigned by your Admin to audit another lab. You'll be provided with a simple, guided checklist to verify their implementation.
                    </p>
                </div>
            ),
        },
        {
            id: "impact",
            title: "Impact Tracking",
            icon: <TrendingUp className="w-5 h-5 text-rose-600" />,
            content: (
                <div className="space-y-4">
                    <p>View the fruits of your labor in the <strong>Estimated Impact Report</strong>. This report visualises your savings in:</p>
                    <div className="flex flex-wrap gap-2 text-sm">
                        <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">Carbon (CO2e)</span>
                        <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-800 border border-blue-200">Water Preservation</span>
                        <span className="px-3 py-1 rounded-full bg-orange-100 text-orange-800 border border-orange-200">Waste Reduction</span>
                    </div>
                </div>
            ),
        },
    ];

    return (
        <GuideLayout
            pageTitle="User Guide"
            backPath="/dashboard"
            backLabel="Back to Dashboard"
            sidebarTitle="Navigation"
            sections={sections}
            theme="emerald"
            cardVariant="secondary"
            showSupportBox={true}
        />
    );
};

UserGuidePage.displayName = "UserGuidePage";

export default UserGuidePage;

